import { PageContainer } from '@/components/page/PageContainer';
import { PageSizer, SizerMode } from '@/components/page/PageSizer';
import { DayPicker } from '@/components/shared/date/DayPicker';
import { TableLoader } from '@/components/shared/table/TableLoader';
import { dateToApiFormat, secondsToTimeString } from '@/helpers/dateHelper';
import { useMyShiftDetailsForDay } from '@/services/employee/useShiftDetailsForDay';
import { useTasks } from '@/services/tasks/useTasks';
import { FunctionComponent, Suspense, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import strings from '../ess.translations';
import { ViewShiftDetailsHeader } from './ViewShiftDetailsHeader';

const ShiftRow: FunctionComponent<{ time: string; label: string }> = ({ time, label }) => (
  <div className="flex space-x-2">
    <p className="w-32 font-semibold">{time}</p>
    <p className="w-32">{label}</p>
  </div>
);

const ViewShiftDetails: FunctionComponent = () => {
  const { date } = useParams<{ date: string }>();
  const navigate = useNavigate();

  if (!date) {
    throw new Error('Date parameter is required');
  }

  const dateObj = useMemo(() => new Date(date), [date]);
  const [selectedDate, setSelectedDate] = useState<Date>(dateObj);

  const data = useMyShiftDetailsForDay(selectedDate);

  useEffect(() => {
    setSelectedDate(dateObj);
  }, [dateObj]);

  const onChangeDate = (newDate: Date) => {
    setSelectedDate(newDate);
    navigate(`/ess/my-calendar/${dateToApiFormat(newDate)}/shift-details`, { replace: true });
  };

  const tasks = useTasks();

  const timelineItems = useMemo(() => {
    if (!data) return [];

    const breaks = (data.breaks ?? []).map(b => ({
      start: b.start,
      end: b.end,
      type: 'break' as const,
      label: strings.viewShiftDetails.breakLabel
    }));

    const taskData = (data.timeSlots ?? [])
      .filter(slot => {
        if (!slot.properties) return false;
        try {
          return JSON.parse(slot.properties).type === 'task';
        } catch {
          return false;
        }
      })
      .map(slot => {
        const context = slot.properties ? JSON.parse(slot.properties).context : undefined;
        const lookupTask = context ? tasks.find(t => t.id === context) : undefined;
        return {
          start: slot.start,
          end: slot.end,
          type: 'task' as const,
          label: lookupTask?.displayName ?? lookupTask?.name
        };
      });

    return [...breaks, ...taskData].sort((a, b) => a.start - b.start);
  }, [data, tasks]);

  return (
    <PageContainer customHeader={<ViewShiftDetailsHeader />}>
      <div className="bg-white border-b-2">
        <PageSizer mode={SizerMode.PAGE}>
          <DayPicker value={selectedDate} onChange={onChangeDate} />
        </PageSizer>
      </div>
      <Suspense fallback={<TableLoader />}>
        <div className="overflow-y-hidden flex flex-col bg-white">
          <PageSizer mode={SizerMode.PAGE} className="flex-1 overflow-auto">
            {data ? (
              <div className="flex flex-col gap-2">
                <ShiftRow time={secondsToTimeString(data.start)} label={strings.viewShiftDetails.shiftStart} />

                {timelineItems.map((item, index) => (
                  <ShiftRow
                    key={index}
                    time={`${secondsToTimeString(item.start)} - ${secondsToTimeString(item.end)}`}
                    label={item.label ?? ''}
                  />
                ))}

                <ShiftRow time={secondsToTimeString(data.end)} label={strings.viewShiftDetails.shiftEnd} />
              </div>
            ) : (
              <p className="font-semibold">No shift details available</p>
            )}
          </PageSizer>
        </div>
      </Suspense>
    </PageContainer>
  );
};

export default ViewShiftDetails;
